import { Schema } from 'effect';

import { ErrorTag } from './index';

/**
 * Sylius API Constraint Violation Codes
 * These codes are returned in violations[].code field of 422 responses
 */
export const ViolationCode = {
  // Payment method errors
  PaymentMethodEligibility: 'sylius.order.payment_method_eligibility',

  // Product eligibility errors
  ProductEligibility: 'sylius.order.product_eligibility',

  // Shipping method errors
  ShippingMethodEligibility: 'sylius.order.shipping_method_eligibility',
  ShippingMethodNotAvailable: 'sylius.order.shipping_method_not_available',

  // Address errors
  AddressRequirement: 'sylius.order.address_requirement',

  // Currency errors
  CurrencyCodeNotValid: 'sylius.order.currency_code.not_valid',

  // Cart item errors
  CartItemNotAvailable: 'sylius.cart_item.not_available',
  CartItemInsufficientStock: 'sylius.cart_item.insufficient_stock',
  CartItemQuantityNotInRange: 'sylius.cart_item.quantity.not_in_range',
  CartItemVariantNotAvailable: 'sylius.cart_item.variant.not_available',

  // Order errors
  OrderNotEmpty: 'sylius.order.not_empty',

  // Product variant errors
  ProductVariantCodeNotExist: 'sylius.product_variant.code.not_exist',
  ProductVariantNotExist: 'sylius.product_variant.not_exist',
  ProductVariantNotSufficient: 'sylius.product_variant.not_sufficient',

  // Order state transition errors (require new token)
  OrderInvalidStateTransition: 'sylius.order.invalid_state_transition',
  OrderCartItemsImmutable: 'sylius.order.cart_items_immutable',
} as const;

export type ViolationCode = (typeof ViolationCode)[keyof typeof ViolationCode];

/**
 * Violation codes that should trigger order token invalidation
 */
export const requiresNewTokenViolationCodes = [
  ViolationCode.OrderInvalidStateTransition,
  ViolationCode.OrderCartItemsImmutable,
] as const;

/**
 * Check if violation code requires a new order token
 */
export const isNewTokenRequiredViolationCode = (code: string): boolean =>
  requiresNewTokenViolationCodes.some((c) => c === code);

/**
 * Whether a refusal's codes say the order can no longer be written to.
 *
 * The list-shaped reading of {@link isNewTokenRequiredViolationCode}: a refusal
 * carries every code the backend sent with it, and one code naming a closed
 * order is enough to spend the token.
 *
 * A refusal carrying no codes answers `false`, and that is the honest answer
 * rather than a gap. The backend said nothing about the order's state, so this
 * predicate cannot either — whoever asked must look at the order itself.
 */
export const requiresNewToken = (codes: ReadonlyArray<string>): boolean =>
  codes.some(isNewTokenRequiredViolationCode);

/**
 * Order Violation Errors - Yieldable Effect Errors
 * These errors map 1:1 with Sylius API constraint violation codes
 */

export class PaymentMethodEligibilityError extends Schema.TaggedError<PaymentMethodEligibilityError>()(
  ErrorTag.PaymentMethodEligibilityError,
  {
    code: Schema.Literal(ViolationCode.PaymentMethodEligibility),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class ProductEligibilityError extends Schema.TaggedError<ProductEligibilityError>()(
  ErrorTag.ProductEligibilityError,
  {
    code: Schema.Literal(ViolationCode.ProductEligibility),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class ShippingMethodEligibilityError extends Schema.TaggedError<ShippingMethodEligibilityError>()(
  ErrorTag.ShippingMethodEligibilityError,
  {
    code: Schema.Literal(ViolationCode.ShippingMethodEligibility),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class ShippingMethodNotAvailableError extends Schema.TaggedError<ShippingMethodNotAvailableError>()(
  ErrorTag.ShippingMethodNotAvailableError,
  {
    code: Schema.Literal(ViolationCode.ShippingMethodNotAvailable),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class AddressRequirementError extends Schema.TaggedError<AddressRequirementError>()(
  ErrorTag.AddressRequirementError,
  {
    code: Schema.Literal(ViolationCode.AddressRequirement),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class CurrencyCodeNotValidError extends Schema.TaggedError<CurrencyCodeNotValidError>()(
  ErrorTag.CurrencyCodeNotValidError,
  {
    code: Schema.Literal(ViolationCode.CurrencyCodeNotValid),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class CartItemNotAvailableError extends Schema.TaggedError<CartItemNotAvailableError>()(
  ErrorTag.CartItemNotAvailableError,
  {
    code: Schema.Literal(ViolationCode.CartItemNotAvailable),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class CartItemInsufficientStockError extends Schema.TaggedError<CartItemInsufficientStockError>()(
  ErrorTag.CartItemInsufficientStockError,
  {
    code: Schema.Literal(ViolationCode.CartItemInsufficientStock),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class CartItemQuantityNotInRangeError extends Schema.TaggedError<CartItemQuantityNotInRangeError>()(
  ErrorTag.CartItemQuantityNotInRangeError,
  {
    code: Schema.Literal(ViolationCode.CartItemQuantityNotInRange),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class CartItemVariantNotAvailableError extends Schema.TaggedError<CartItemVariantNotAvailableError>()(
  ErrorTag.CartItemVariantNotAvailableError,
  {
    code: Schema.Literal(ViolationCode.CartItemVariantNotAvailable),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class OrderNotEmptyError extends Schema.TaggedError<OrderNotEmptyError>()(
  ErrorTag.OrderNotEmptyError,
  {
    code: Schema.Literal(ViolationCode.OrderNotEmpty),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class ProductVariantCodeNotExistError extends Schema.TaggedError<ProductVariantCodeNotExistError>()(
  ErrorTag.ProductVariantCodeNotExistError,
  {
    code: Schema.Literal(ViolationCode.ProductVariantCodeNotExist),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class ProductVariantNotExistError extends Schema.TaggedError<ProductVariantNotExistError>()(
  ErrorTag.ProductVariantNotExistError,
  {
    code: Schema.Literal(ViolationCode.ProductVariantNotExist),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class ProductVariantNotSufficientError extends Schema.TaggedError<ProductVariantNotSufficientError>()(
  ErrorTag.ProductVariantNotSufficientError,
  {
    code: Schema.Literal(ViolationCode.ProductVariantNotSufficient),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class OrderInvalidStateTransitionError extends Schema.TaggedError<OrderInvalidStateTransitionError>()(
  ErrorTag.OrderInvalidStateTransitionError,
  {
    code: Schema.Literal(ViolationCode.OrderInvalidStateTransition),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

export class OrderCartItemsImmutableError extends Schema.TaggedError<OrderCartItemsImmutableError>()(
  ErrorTag.OrderCartItemsImmutableError,
  {
    code: Schema.Literal(ViolationCode.OrderCartItemsImmutable),
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

/**
 * Unknown violation error - for violation codes we don't recognize
 */
export class UnknownViolationError extends Schema.TaggedError<UnknownViolationError>()(
  ErrorTag.UnknownViolationError,
  {
    code: Schema.String,
    propertyPath: Schema.optional(Schema.String),
    message: Schema.optional(Schema.String),
  }
) {}

/**
 * Union of all order violation errors
 */
export const OrderViolationError = Schema.Union([
  PaymentMethodEligibilityError,
  ProductEligibilityError,
  ShippingMethodEligibilityError,
  ShippingMethodNotAvailableError,
  AddressRequirementError,
  CurrencyCodeNotValidError,
  CartItemNotAvailableError,
  CartItemInsufficientStockError,
  CartItemQuantityNotInRangeError,
  CartItemVariantNotAvailableError,
  OrderNotEmptyError,
  ProductVariantCodeNotExistError,
  ProductVariantNotExistError,
  ProductVariantNotSufficientError,
  OrderInvalidStateTransitionError,
  OrderCartItemsImmutableError,
  UnknownViolationError,
]);

export type OrderViolationError = Schema.Schema.Type<typeof OrderViolationError>;
